import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lightning, SignOut, BookmarkSimple, Star, Crown, Medal } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { startCheckout, PaywallDialog } from "./PaywallDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function openPortal() {
  try {
    const { data } = await axios.post(`${API}/payments/portal`,
      { origin_url: window.location.origin }, { withCredentials: true });
    window.location.href = data.portal_url;
  } catch (err) {
    const d = err.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Couldn't open the billing portal");
  }
}

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showPaywall, setShowPaywall] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to={user ? "/app" : "/"} className="flex items-center gap-2" data-testid="header-logo">
          <Lightning size={26} weight="fill" className="text-[#00E676]" />
          <span className="font-display text-2xl font-black uppercase tracking-tight">
            Parlay<span className="text-[#00E676]">Labs</span>
          </span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4">
          {user ? (
            <>
              <Link to="/best" data-testid="nav-best-link"
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-[#FFD700] transition-colors px-3 py-2">
                <Star size={18} weight="bold" />
                <span className="hidden sm:inline">Best Value</span>
              </Link>
              <Link to="/saved" data-testid="nav-saved-link"
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-300 hover:text-white transition-colors px-3 py-2">
                <BookmarkSimple size={18} weight="bold" />
                <span className="hidden sm:inline">Saved</span>
              </Link>
              {user.plan === "pro" ? (
                <button onClick={openPortal} data-testid="pro-badge" title="Manage subscription"
                  className="flex items-center gap-1 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] px-3 py-1 text-xs font-black uppercase hover:bg-[#FFD700]/20 transition-colors">
                  <Crown size={14} weight="fill" /> Pro
                </button>
              ) : user.plan === "amateur" ? (
                <>
                  <button onClick={openPortal} data-testid="amateur-badge" title="Manage subscription"
                    className="flex items-center gap-1 rounded-full bg-[#2563EB]/10 border border-[#2563EB]/40 text-[#60A5FA] px-3 py-1 text-xs font-black uppercase hover:bg-[#2563EB]/20 transition-colors">
                    <Medal size={14} weight="fill" /> Amateur
                  </button>
                  <button onClick={() => startCheckout(API, "pro_monthly")} data-testid="header-gopro-btn"
                    className="rounded-full bg-[#FFD700] text-black font-bold px-4 py-1.5 text-xs hover:scale-105 active:scale-95 transition-transform">
                    Go Pro
                  </button>
                </>
              ) : (
                <button onClick={() => setShowPaywall(true)} data-testid="header-upgrade-btn"
                  className="rounded-full bg-[#FFD700] text-black font-bold px-4 py-1.5 text-xs hover:scale-105 active:scale-95 transition-transform">
                  Upgrade
                </button>
              )}
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn"
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-[#FF3366] transition-colors px-3 py-2">
                <SignOut size={18} weight="bold" />
                <span className="hidden sm:inline">Log out</span>
              </button>
            </>
          ) : (
            <>
              <Link to="/pricing" data-testid="nav-pricing-link"
                className="text-sm font-semibold text-slate-300 hover:text-white transition-colors px-3 py-2">
                Pricing
              </Link>
              <Link to="/auth" data-testid="nav-login-link"
                className="rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] transition-colors px-5 py-2 text-sm font-bold">
                Sign in
              </Link>
            </>
          )}
        </nav>
      </div>
      <PaywallDialog open={showPaywall} onOpenChange={setShowPaywall} API={API} />
    </header>
  );
};

export const Disclaimer = () => (
  <footer className="border-t border-white/10 mt-16">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 text-xs text-slate-500 leading-relaxed" data-testid="legal-disclaimer">
      <p className="font-bold uppercase tracking-[0.2em] text-slate-400 mb-2">21+ · For entertainment and analytics only</p>
      <p>
        ParlayLabs is a sports analytics tool for entertainment purposes. We are not a sportsbook and do not accept
        or place wagers. Odds shown are simulated market data. Probabilities and EV are model estimates, not guarantees.
        If you or someone you know has a gambling problem, call 1-800-GAMBLER.
      </p>
      <div className="flex gap-5 mt-4 font-semibold">
        <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
        <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
        <Link to="/responsible" className="hover:text-white transition-colors" data-testid="footer-responsible-link">Responsible Gaming</Link>
      </div>
    </div>
  </footer>
);
